import { getJSON } from 'common/services/api'
import { useQuery } from 'react-query'

export type FlagData = {
  enabled: boolean
  value: unknown
}
type Flags<T extends Array<string>> = { [K in T[number]]: FlagData } & {
  [key: string]: FlagData
}

type UseFeatureFlagReturn<T extends Array<string>> = {
  data: Flags<T>
  isLoading: boolean
  isSuccess: boolean
  isError: boolean
}

const useFeatureFlags = <T extends Array<string>>({
  flagNames
}: {
  flagNames: T
}): UseFeatureFlagReturn<T> => {
  const {
    data: response,
    isLoading,
    isSuccess,
    isError
  } = useQuery<{ data: Flags<T> }>(
    ['featureFlags'],
    () => getJSON({ url: `/services/flagsmith` }),
    {
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5 // 5 minutes
    }
  )

  return {
    data: {
      ...(Object.fromEntries(
        flagNames.map((flagname) => [
          flagname,
          { enabled: false, value: undefined }
        ])
      ) as Flags<T>),
      ...(response?.data ?? {})
    },
    isLoading,
    isSuccess,
    isError
  }
}

export default useFeatureFlags
